<template>
  <div
    v-if="!$yhcefs.$userInputData.deliveryDateDisplayErrorMessage"
    class="yhc-ef-datetime-select"
  >
    <div
      v-html="returnTimeOptionMessage"
      class="mb-2 yhc-ef-datetime-select--desc"
    ></div>
    <div
      v-if="canSelectTimeOption && !isSameDayDelivery && !isTimeSlotDisabledByPeakSeason"
      class="yhc-ef-datetime-select--option--wrapper"
    >
      <div
        v-for="({ value, label, price }, index) in timeSelection"
        :key="value"
        class="yhc-ef-datetime-select--option yhc-option-item"
        :class="{
          [`yhc-ef-datetime-select--option-${value}`]: true,
          active: value === selectedTimeOption,
        }"
        @click.prevent="() => $emit('update:timeOption', value)"
      >
        <ficon :type="value === selectedTimeOption ? 'check-circle' : 'circle'" />
        {{ label }}
        <span
          v-if="value !== 'none'"
          class="yhc-ef-datetime-select--option--price small"
          >(+¥{{ (price || 0).toLocaleString() }})</span
        >
      </div>
    </div>
    <div
      v-if="justInTimeOptionPrice && !isSameDayDelivery"
      class="yhc-ef-datetime-select--jit-option--wrapper pb-2"
    >
      <p class="yhc-ef-datetime-select--jit-option--desc">
        {{ justInTimeOption.description }}
        <YhcPublicWebHtmlContentDisplay
          v-if="justInTimeOption.notice"
          class="d-block text-muted small yhc-richtext-content mt-1"
          :html-content="justInTimeOption.notice"
        />
      </p>
      <div class="yhc-ef-datetime-select--jit-option">
        <label
          v-for="tf in [false, true]"
          class="yhc-ef-datetime-select--jit-option-item yhc-option-item"
          :class="{
            active: tf === selectedUseJustInTimeOption,
          }"
          @click.prevent="() => $emit('update:useJustInTimeOption', tf)"
        >
          <ficon :type="tf === selectedUseJustInTimeOption ? 'check-circle' : 'circle'" />
          {{ tf ? '1時間枠指定する' : '指定なし' }}
          <span
            v-if="tf === true"
            class="yhc-ef-datetime-select--jit-option-item--price small"
            >(+¥{{ justInTimeOptionPrice.toLocaleString() }})</span
          >
        </label>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { PropType } from 'vue'
import {
  ModelYhcMastersByZipcode,
  ModelYhcOptions,
  YhcOptionsKey,
} from '../../../../fireModels/yhcDataTypes'
import {
  judgeCompanyOfAreaFromYhcZipMaster,
  YhcEFTimeOptionKey,
  yhcEFTimeOptions,
  yhcEFTimeOptionsByTimeBandCode,
} from '../../YhcEFUserInputDataHandler'
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontService'

/**
 * # 時間帯指定 選択のコンポーネント
 *
 * ## "時間帯指定" の選択肢を出す Logic
 * - 配送元, 先 それぞれの zipMaster 内の "時間帯区分" `timeBand` により 選択肢 & 料金 が決定される
 *   - 0: ４区分 (指定なし, 午前, 12-15時, 15-18時, 18-21時)
 *   - 1: ３区分[無料] (指定なし, 午前, 12-18時, 18-21時)
 *   - 2: ３区分[有料] (指定なし, +5,500円)
 *   - 3: 指定不可
 *   - 4: ４区分 2027-01-01〜 (指定なし, 8-12時, 12-16時, 16-19時, 18-21時。各枠 +1,100円税込)
 *   - 5: 指定不可 2027-01-01〜 (指定不可 + 1時間枠 JIT)
 *
 * ## "ジャスト・イン・タイム" のオプション選択肢を出すロジック
 * - zipMaster 内の "時間帯区分" `timeBand` が 0 / 1 / 4 / 5 の場合のみ 下記料金で 1時間枠指定で可能
 *   - 0 / 4: 4区分エリア +11,000円（just_on_time_0）
 *   - 1: 3区分エリア +16,500円
 *   - 5: 指定不可エリア +22,000円（just_on_time_unable）
 *
 */
export default {
  name: 'YhcEFDatetimeSelect',
  props: {
    fromOrTo: {
      type: String as PropType<'from' | 'to'>,
      required: true,
    },
    selectedTimeOption: {
      type: String as PropType<string>,
      required: true,
    },
    selectedUseJustInTimeOption: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    zipCodeMaster: {
      type: Object as PropType<ModelYhcMastersByZipcode | null>,
      required: true,
    },
  },
  inject: ['YhcEstimateFormFrontServiceInstance'],
  computed: {
    $yhcefs(): YhcEstimateFormFrontService {
      return this.YhcEstimateFormFrontServiceInstance
    },
    timeOptionByTimeBandCode() {
      return yhcEFTimeOptionsByTimeBandCode[this.zipCodeMaster?.timeBand]
    },
    canSelectTimeOption(): boolean {
      // YTC エリアの場合は 時間指定不可 に
      if (judgeCompanyOfAreaFromYhcZipMaster(this.zipCodeMaster) === 'ytc') {
        return false
      }
      return this.timeSelection.length > 1
    },
    isSameDayDelivery(): boolean {
      return this.$yhcefs.$userInputData.isSameDayPickupAndDelivery
    },
    selectTimeOptionMessage(): {
      can: string
      cannot: string
      sameDayDelivery: string
      peakSeason: string
    } {
      return {
        can: 'ご指定エリアでは、以下の時間帯からお選びいただけます。',
        cannot: 'ご指定エリアでは 時間帯指定ができません。',
        sameDayDelivery: 'お預かり日当日配達をご希望の場合は、時間帯を指定できません。',
        peakSeason: '引越シーズンのため、時間帯指定ができません。',
      }
    },
    returnTimeOptionMessage(): string {
      // 時間帯指定不可エリア
      if (!this.canSelectTimeOption) {
        return this.selectTimeOptionMessage.cannot
      }

      // お預かり日当日配達
      if (this.isSameDayDelivery) {
        return this.selectTimeOptionMessage.sameDayDelivery
      }

      // 繁忙期シーズン（有料・無料問わず時間帯指定は全て不可）
      if (this.isTimeSlotDisabledByPeakSeason) {
        return this.selectTimeOptionMessage.peakSeason
      }

      // 通常時
      return this.selectTimeOptionMessage.can
    },
    timeSelection(): {
      value: YhcEFTimeOptionKey
      label: string
      price: number
    }[] {
      if (!this.timeOptionByTimeBandCode) {
        return []
      }
      return (this.timeOptionByTimeBandCode.selectableTimeOptionKeys || []).map(
        (timeOptionKey) => {
          return {
            value: timeOptionKey,
            label: yhcEFTimeOptions[timeOptionKey].label,
            price:
              this.$yhcefs.generalOptionsByKey[
                this.timeOptionByTimeBandCode.additionalPriceByTimeBand?.[
                  timeOptionKey
                ] as YhcOptionsKey
              ]?.price || 0,
          }
        },
      )
    },
    justInTimeOption(): ModelYhcOptions {
      return this.$yhcefs.generalOptionsByKey[
        this.timeOptionByTimeBandCode?.justInTimeOption?.price
      ]
    },
    justInTimeOptionPrice(): number | null {
      return this.justInTimeOption?.price || null
    },
    // お預かり側: お預かり日が繁忙期に掛かっているとき true。お届け側: お届け日が繁忙期に掛かっているとき true。
    isTimeSlotDisabledByPeakSeason(): boolean {
      const uid = this.$yhcefs.$userInputData
      if (this.fromOrTo === 'from') {
        return uid.pickupDateInPeakSeason
      }
      return uid.deliveryDateInPeakSeason
    },
  },
  mounted() {
    this.changeCanSelectTimeOption()
  },
  watch: {
    canSelectTimeOption() {
      this.changeCanSelectTimeOption()
    },
    isTimeSlotDisabledByPeakSeason: {
      handler(newValue) {
        if (newValue) {
          this.$emit('update:timeOption', yhcEFTimeOptions.none.value)
        }
      },
      immediate: true,
    },
  },
  methods: {
    changeCanSelectTimeOption() {
      this.$emit('update:canSelectTimeOption', this.canSelectTimeOption)
    },
  },
}
</script>
<style lang="scss">
.yhc-ef-datetime-select--desc {
  font-size: 12px;
  font-weight: bold;
}

.yhc-ef-datetime-select--option--wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: var(--yhc-base-pad-sm);
  margin-bottom: var(--yhc-base-pad);

  .yhc-ef-datetime-select--option-am {
    border-color: var(--yhc-color-yellow);
  }

  .yhc-ef-datetime-select--option-am8_12 {
    border-color: var(--yhc-color-yellow);
  }

  .yhc-ef-datetime-select--option-pm12_15 {
    border-color: var(--yhc-color-pink);

    &.active {
      border-color: var(--yhc-color-pink);
      background-color: var(--yhc-color-pink);
      color: white;
    }
  }

  .yhc-ef-datetime-select--option-pm12_16 {
    border-color: var(--yhc-color-pink);

    &.active {
      border-color: var(--yhc-color-pink);
      background-color: var(--yhc-color-pink);
      color: white;
    }
  }

  .yhc-ef-datetime-select--option-pm12_18 {
    border-color: var(--yhc-color-pink);

    &.active {
      border-color: var(--yhc-color-pink);
      background-color: var(--yhc-color-pink);
      color: white;
    }
  }

  .yhc-ef-datetime-select--option-pm15_18 {
    border-color: var(--yhc-color-green);

    &.active {
      border-color: var(--yhc-color-green);
      background-color: var(--yhc-color-green);
      color: white;
    }
  }

  .yhc-ef-datetime-select--option-pm16_19 {
    border-color: var(--yhc-color-green);

    &.active {
      border-color: var(--yhc-color-green);
      background-color: var(--yhc-color-green);
      color: white;
    }
  }

  .yhc-ef-datetime-select--option-pm18_21 {
    border-color: var(--yhc-color-blue);

    &.active {
      border-color: var(--yhc-color-blue);
      background-color: var(--yhc-color-blue);
      color: white;
    }
  }
}

.yhc-ef-datetime-select--jit-option--wrapper {
  border-top: 1px solid var(--bs-gray-200);
  padding-top: var(--yhc-base-pad);

  .yhc-ef-datetime-select--jit-option--desc {
    font-size: 12px;
    font-weight: bold;
  }

  .yhc-ef-datetime-select--jit-option {
    display: flex;
    gap: var(--yhc-base-pad-sm);
    flex-wrap: wrap;
    margin-top: 10px;

    .yhc-ef-datetime-select--jit-option-item {
    }
  }
}
</style>
